import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PhoneCall,
  Save,
  Zap,
  CheckCircle2,
  AlertTriangle,
  RefreshCcw,
  Eye,
  EyeOff,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Config admin para a integração Linkus / Yeastar P-Series.
 * Guarda base_url + client_id + client_secret. Botão "Testar ligação"
 * chama o backend que troca as credenciais por um access_token real.
 */
export default function LinkusSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    base_url: "",
    client_id: "",
    client_secret: "",
    webhook_secret: "",
    enabled: true,
    mode: "poll",
  });
  const [secretIsSet, setSecretIsSet] = useState(false);
  const [webhookSecretIsSet, setWebhookSecretIsSet] = useState(false);

  const load = async () => {
    try {
      const [{ data: cfg }, { data: st }] = await Promise.all([
        api.get("/linkus/config"),
        api.get("/linkus/status"),
      ]);
      setForm({
        base_url: cfg.base_url || "",
        client_id: cfg.client_id || "",
        client_secret: "",
        webhook_secret: "",
        enabled: cfg.enabled ?? true,
        mode: cfg.mode || "poll",
      });
      setSecretIsSet(!!cfg.client_secret_set);
      setWebhookSecretIsSet(!!cfg.webhook_secret_set);
      setStatus(st);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar config.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/linkus/config", form);
      toast.success("Configuração guardada.");
      setForm((f) => ({ ...f, client_secret: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gravar.");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/linkus/test-connection");
      toast.success(
        `Ligação OK — token válido por ${Math.round(
          (data.expires_in_seconds || 1800) / 60,
        )} min.`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na ligação.");
    } finally {
      setTesting(false);
    }
  };

  const syncNow = async (forceFull = false) => {
    setSyncing(true);
    try {
      const { data } = await api.post(
        `/linkus/sync-now?force_full=${forceFull ? "true" : "false"}`,
      );
      if (data.skipped) {
        toast.warning(`Sync ignorada: ${data.skipped}`);
      } else if (data.errors?.length) {
        toast.error(`Sync com erros: ${data.errors[0]}`);
      } else {
        toast.success(
          `Sync OK — ${data.cdrs_fetched} CDR(s) recebidos, ${data.cdrs_inserted} novos, ${data.discrepancies_upserted} discrepância(s).`,
        );
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao sincronizar.");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">A carregar…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="linkus-settings">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <PhoneCall className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">
            Linkus (Yeastar PBX)
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Importa registos de chamadas (CDR) do PBX e compara com o estado
            Zoho de cada lead. Actualiza semi-automaticamente o Zoho quando
            detecta divergências.
          </p>
        </div>
      </div>

      {/* Config card */}
      <div className="bg-card rounded-lg border border-border divide-y divide-slate-100">
        {/* Mode selector */}
        <div className="p-5">
          <h2 className="text-sm font-semibold text-foreground mb-1">
            Modo de sincronização
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Escolhe como as chamadas do PBX chegam à Caring.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[
              {
                key: "poll",
                title: "Pull automático",
                desc: "A Caring puxa CDRs a cada 15 min. Exige IP allowlist no PBX.",
              },
              {
                key: "webhook",
                title: "Webhook em tempo real",
                desc: "O PBX empurra cada chamada. Sem allowlist. Sem backfill.",
              },
              {
                key: "both",
                title: "Ambos",
                desc: "Redundância — resistente a falhas de rede. Idempotente por UID.",
              },
            ].map((m) => {
              const active = form.mode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  data-testid={`linkus-mode-${m.key}`}
                  onClick={() => setForm({ ...form, mode: m.key })}
                  className={`text-left rounded-md border-2 px-3 py-3 transition-all ${
                    active
                      ? "border-primary bg-primary/5 ring-1 ring-[#1D4ED8]/20"
                      : "border-border bg-card hover:border-border"
                  }`}
                >
                  <div className={`text-sm font-semibold ${active ? "text-primary" : "text-foreground"}`}>
                    {m.title}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 leading-snug">
                    {m.desc}
                  </div>
                </button>
              );
            })}
          </div>
          {form.mode === "webhook" && (
            <div className="mt-3 rounded-md border border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))] px-3 py-2 text-xs text-[hsl(var(--success))]">
              <b>Modo Webhook-only:</b> não é feito pull ao PBX. Os campos Base URL / Client ID / Client Secret ficam opcionais — só o <b>Webhook Secret</b> é obrigatório.
              {status?.last_webhook_at ? (
                <div className="mt-1">
                  Último webhook recebido: <b>{new Date(status.last_webhook_at).toLocaleString("pt-PT")}</b>
                </div>
              ) : (
                <div className="mt-1 text-[hsl(var(--success))]/80">
                  Ainda não recebemos nenhum webhook. Configura no PBX (URL + secret) e faz uma chamada de teste.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-5">
          <h2 className="text-sm font-semibold text-foreground mb-4">
            Credenciais da API {form.mode === "webhook" && <span className="text-xs font-normal text-muted-foreground">(opcional em modo Webhook)</span>}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground">
                Base URL do PBX
              </label>
              <input
                type="text"
                data-testid="linkus-base-url"
                placeholder="https://caring.p-cs.yeastar.com (Cloud) ou https://192.168.x.x:8088 (On-Prem)"
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                className="mt-1.5 block w-full rounded-md border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                O sistema acrescenta <code>/openapi/v1.0</code> automaticamente
                se não estiver presente.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground">
                Client ID (Identificação do cliente)
              </label>
              <input
                type="text"
                data-testid="linkus-client-id"
                placeholder="Ex.: FiyrXVMc58YF5e16EU6KgnR4E9MkXDxV"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="mt-1.5 block w-full rounded-md border border-border px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground">
                Client Secret (Segredo do cliente)
              </label>
              <div className="mt-1.5 relative">
                <input
                  type={showSecret ? "text" : "password"}
                  data-testid="linkus-client-secret"
                  placeholder={
                    secretIsSet
                      ? "•••••••••• (deixa vazio para manter o guardado)"
                      : "Cola aqui o Segredo do cliente do PBX"
                  }
                  value={form.client_secret}
                  onChange={(e) =>
                    setForm({ ...form, client_secret: e.target.value })
                  }
                  className="block w-full rounded-md border border-border px-3 py-2 pr-10 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-muted-foreground"
                  title={showSecret ? "Esconder" : "Ver"}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {secretIsSet && (
                <p className="mt-1 text-xs text-[hsl(var(--success))]">
                  <CheckCircle2 className="inline w-3 h-3 mr-1" />
                  Segredo já guardado. Deixa em branco para o manter.
                </p>
              )}
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="linkus-enabled"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="rounded border-border text-primary focus:ring-[#1D4ED8]/20"
              />
              <span className="text-sm text-foreground">
                Sync automática activada (a cada 15 min)
              </span>
            </label>
          </div>

          {/* Webhook em tempo real */}
          <div className="mt-6 pt-5 border-t border-border/60">
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Webhook em tempo real (opcional)
            </h3>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              Configura o PBX para nos empurrar cada chamada assim que
              termina — evita esperar pelo próximo pull de 15 min.
              Cola aqui o mesmo <em>Secret</em> que colocaste no PBX em
              <code className="mx-1 px-1 py-0.5 rounded bg-muted font-mono">Integrações → API → Webhook Event Push</code>.
            </p>

            <label className="block text-sm font-medium text-foreground">
              Webhook Secret
            </label>
            <input
              type="password"
              data-testid="linkus-webhook-secret"
              placeholder={
                webhookSecretIsSet
                  ? "•••••••••• (deixa vazio para manter o guardado)"
                  : "Cola aqui o Secret do Webhook do PBX"
              }
              value={form.webhook_secret}
              onChange={(e) =>
                setForm({ ...form, webhook_secret: e.target.value })
              }
              className="mt-1.5 block w-full rounded-md border border-border px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
            />
            {webhookSecretIsSet && (
              <p className="mt-1 text-xs text-[hsl(var(--success))]">
                <CheckCircle2 className="inline w-3 h-3 mr-1" />
                Secret webhook já guardado.
              </p>
            )}

            <div className="mt-3 rounded-md border border-[hsl(var(--info))]/25 bg-[hsl(var(--info-bg))] px-3 py-2 text-xs text-[hsl(var(--info))]">
              <b>URL a colar no PBX:</b>{" "}
              <code className="ml-1 font-mono break-all">
                {typeof window !== "undefined" && window.location
                  ? `${window.location.origin}/api/webhooks/yeastar`
                  : "https://leads.caring.pt/api/webhooks/yeastar"}
              </code>
            </div>

            {/* IP público do backend — imprescindível quando o PBX
                tem "IP Allowlist" activada (erro 70087 IP FORBIDDEN). */}
            {status?.backend_public_ip && (
              <div className="mt-3 rounded-md border border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))] px-3 py-2 text-xs text-[hsl(var(--warning))]">
                <b>IP público do backend Caring:</b>{" "}
                <code
                  className="ml-1 font-mono select-all cursor-pointer hover:bg-[hsl(var(--warning-bg))] px-1 py-0.5 rounded"
                  data-testid="linkus-backend-ip"
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(`${status.backend_public_ip}/32`);
                      toast.success("IP copiado (/32).");
                    }
                  }}
                  title="Clica para copiar (formato /32)"
                >
                  {status.backend_public_ip}
                </code>
                <div className="mt-1 text-[hsl(var(--warning))]/80">
                  Adiciona <code>{status.backend_public_ip}/32</code> em <b>PBX Yeastar → Segurança → API → Allowlist</b> se receberes o erro <code>70087 IP FORBIDDEN</code>.
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2 justify-end">
            {form.mode !== "webhook" && (
              <button
                type="button"
                onClick={test}
                disabled={testing || !form.base_url || !form.client_id || (!secretIsSet && !form.client_secret)}
                data-testid="linkus-test-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-card hover:bg-muted/40 text-sm font-medium disabled:opacity-40"
              >
                <Zap className="w-4 h-4" /> {testing ? "A testar…" : "Testar ligação"}
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="linkus-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold disabled:opacity-40"
            >
              <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Guardar configuração"}
            </button>
          </div>
        </div>

        {/* Status card */}
        {status && (
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-foreground">Estado da sincronização</h2>
              {form.mode !== "webhook" && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => syncNow(false)}
                    disabled={syncing || !status.configured}
                    data-testid="linkus-sync-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-muted/40 text-xs font-medium disabled:opacity-40"
                  >
                    <RefreshCcw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
                    {syncing ? "A sincronizar…" : "Sync agora"}
                  </button>
                <button
                  type="button"
                  onClick={() => syncNow(true)}
                  disabled={syncing || !status.configured}
                  data-testid="linkus-sync-full-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-muted/40 text-xs font-medium disabled:opacity-40"
                  title="Refaz os últimos 30 dias"
                >
                  Full (30d)
                </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
              <StatBadge label="Configurado" value={status.configured ? "Sim" : "Não"}
                          tone={status.configured ? "emerald" : "slate"} />
              <StatBadge label="Activo" value={status.enabled ? "Sim" : "Não"}
                          tone={status.enabled ? "emerald" : "slate"} />
              <StatBadge label="CDRs total" value={status.counters?.cdrs_total ?? 0} />
              <StatBadge label="CDRs → lead" value={status.counters?.cdrs_matched_to_lead ?? 0} />
              <StatBadge label="Pendentes" value={status.counters?.discrepancies_pending ?? 0}
                          tone={(status.counters?.discrepancies_pending || 0) > 0 ? "amber" : "slate"} />
            </div>

            {status.last_sync_at && (
              <div className="mt-3 text-xs text-muted-foreground">
                Última sync: {new Date(status.last_sync_at).toLocaleString("pt-PT")}
                {status.last_sync_stats?.cdrs_fetched !== undefined && (
                  <span className="ml-2">
                    · {status.last_sync_stats.cdrs_fetched} CDR(s) puxados
                    · {status.last_sync_stats.cdrs_inserted} novos
                    · {status.last_sync_stats.leads_matched} lead(s) associados
                  </span>
                )}
              </div>
            )}

            {status.last_sync_stats?.errors?.length > 0 && (
              <div className="mt-3 rounded-md border border-[hsl(var(--danger))]/25 bg-[hsl(var(--danger-bg))] px-3 py-2 text-xs text-[hsl(var(--danger))]">
                <AlertTriangle className="inline w-3.5 h-3.5 mr-1" />
                Última sync com erro: {status.last_sync_stats.errors[0]}
              </div>
            )}

            <div className="mt-4 text-xs text-muted-foreground">
              <a
                href="/linkus/discrepancies"
                className="text-primary hover:underline font-medium"
              >
                Ver painel de divergências →
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBadge({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-border bg-muted/40 text-foreground",
    emerald: "border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]",
    amber: "border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]",
    rose: "border-[hsl(var(--danger))]/25 bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
}
